import type { ChartId } from 'charts/catalog/chartCatalog';
import React, { createContext, ReactElement, ReactNode, useCallback, useContext } from 'react';
import type { SectionSharedFiltersModel } from './useSectionSharedFilters';

const SectionFiltersContext = createContext<SectionSharedFiltersModel | null>(null);

type SectionFiltersProviderProps = Readonly<{
  model: SectionSharedFiltersModel;
  children: ReactNode;
}>;

/** The filters of the dashboard section, for the chart tiles in it. */
export function SectionFiltersProvider({ model, children }: SectionFiltersProviderProps): ReactElement {
  return <SectionFiltersContext.Provider value={model}>{children}</SectionFiltersContext.Provider>;
}

export type ChartSectionFilters = Readonly<{
  /** The chart has its own filters (set in its details), which override the section's. */
  hasCustomFilters: boolean;
  /** Gives the chart the section filters back; none while the section filters cannot be changed (offline). */
  restoreSectionFilters?: () => void;
}>;

/** How the chart stands against the filters of its section; outside a section it has none of its own. */
export function useChartSectionFilters(chartId: ChartId): ChartSectionFilters {
  const model = useContext(SectionFiltersContext);
  const restore = model?.restoreSectionFilters;
  const restoreSectionFilters = useCallback((): void => restore?.(chartId), [restore, chartId]);
  return {
    hasCustomFilters: model?.customChartIds.has(chartId) ?? false,
    restoreSectionFilters: model && !model.disabled ? restoreSectionFilters : undefined,
  };
}
